import React from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts';
import { WorkspaceContextSetter } from '../context/WorkspaceContext';
import { themeColors } from '../theme';
import PlotExportBar from './PlotExportBar';

const ProfileTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#0d1117', border: '1px solid #2a3f55', borderRadius: 4,
      padding: '10px 14px', fontFamily: 'IBM Plex Mono, monospace', fontSize: 13,
    }}>
      <div style={{ color: '#8ba3bb', marginBottom: 6 }}>x = {Number(label).toFixed(0)} nm</div>
      {payload.map(p => (
        <div key={p.name} style={{ color: p.color, marginBottom: 2 }}>
          {p.name}: {Number(p.value).toExponential(3)}
        </div>
      ))}
    </div>
  );
};

const logTick = v => {
  if (!isFinite(v)) return '';
  const exp = Math.round(v);
  const sup = String(Math.abs(exp)).split('').map(
    d => '\u2070\u00B9\u00B2\u00B3\u2074\u2075\u2076\u2077\u2078\u2079'[d]
  ).join('');
  return `10${exp < 0 ? '\u207B' : ''}${sup}`;
};

const linTick = d => v => {
  if (!isFinite(v)) return '';
  if (Math.abs(v) >= 1e4 || (Math.abs(v) < 0.01 && v !== 0)) return v.toExponential(1);
  return Number(v).toFixed(d);
};

/** Generate evenly spaced x-axis ticks from 0 to xMax. */
function xTicks(xMax) {
  if (xMax <= 0) return [0];
  // Pick a nice step: 100, 200, 500, 1000, etc.
  const raw = xMax / 6;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const steps = [1, 2, 5, 10];
  const step = mag * steps.find(s => s * mag >= raw) || mag * 10;
  const ticks = [];
  for (let t = 0; t <= xMax + step * 0.01; t += step)
    ticks.push(Math.round(t));
  return ticks;
}

export default function ProfilesPanel({ results, theme = 'dark' }) {
  const c = themeColors(theme);
  const colors = c;
  const axisStyle = { fill: c.plotText, fontSize: 13, fontFamily: 'IBM Plex Mono' };
  const tickStyle = { fill: c.plotText, fontSize: 12, fontFamily: 'IBM Plex Mono' };
  const profiles = results?.profiles || [];

  // ── Stage 2: publish to WorkspaceContext (profiles block) ────────
  const publish = React.useContext(WorkspaceContextSetter);
  React.useEffect(() => {
    publish('profiles', {
      has_data: profiles.length > 0,
      selected_view: null,    // no sub-view selector yet; future
    });
  }, [publish, profiles.length]);

  if (!profiles.length) {
    return (
      <div className="results-panel">
        <div className="empty-state">
          <div className="empty-icon">&#9781;</div>
          <div className="empty-title">No Profile Data</div>
          <div className="empty-sub">
            Run a simulation to see carrier density, generation, recombination, and electric field profiles.
          </div>
        </div>
      </div>
    );
  }

  const xMax = profiles[profiles.length - 1].x_nm;
  const xTickArr = xTicks(xMax);

  // Carrier densities (log scale)
  const npData = profiles.map(pt => ({
    x: pt.x_nm,
    log_n: pt.n > 0 ? Math.log10(pt.n) : null,
    log_p: pt.p > 0 ? Math.log10(pt.p) : null,
  })).filter(d => d.log_n != null || d.log_p != null);

  // Generation & recombination (log scale)
  const grData = profiles.map(pt => ({
    x: pt.x_nm,
    log_G: pt.G > 0 ? Math.log10(pt.G) : null,
    log_R: pt.R > 0 ? Math.log10(pt.R) : null,
  })).filter(d => d.log_G != null || d.log_R != null);

  // Electric field (linear, V/cm)
  const eData = profiles
    .filter(pt => pt.E_field != null && isFinite(pt.E_field))
    .map(pt => ({ x: pt.x_nm, E: pt.E_field }));

  // Net recombination G-R
  const netGRData = profiles.map(pt => ({
    x: pt.x_nm,
    'G-R': pt.G - pt.R,
  }));

  // Y-axis ranges for carrier density
  const nVals = npData.flatMap(d => [d.log_n, d.log_p]).filter(v => v != null && isFinite(v));
  const npMin = nVals.length ? Math.floor(Math.min(...nVals)) - 1 : 0;
  const npMax = nVals.length ? Math.ceil(Math.max(...nVals)) + 1 : 20;

  // Y-axis for G/R
  const grVals = grData.flatMap(d => [d.log_G, d.log_R]).filter(v => v != null && isFinite(v));
  const grMin = grVals.length ? Math.floor(Math.min(...grVals)) - 1 : 10;
  const grMax = grVals.length ? Math.ceil(Math.max(...grVals)) + 1 : 22;

  const margin = { top: 8, right: 20, bottom: 28, left: 20 };

  const sharedXAxis = (
    <XAxis dataKey="x" type="number" domain={[0, xMax]} ticks={xTickArr}
      tickFormatter={v => v.toFixed(0)} tick={tickStyle}
      label={{ value: 'Position (nm)', position: 'insideBottom', offset: -14, ...axisStyle }} />
  );

  // Per-plot CSV builders
  const csvNP = profiles.map(pt => ({ position_nm: pt.x_nm, n: pt.n, p: pt.p }));
  const csvGR = profiles.map(pt => ({ position_nm: pt.x_nm, G: pt.G, R: pt.R }));
  const csvE  = profiles
    .filter(pt => pt.E_field != null && Number.isFinite(pt.E_field))
    .map(pt => ({ position_nm: pt.x_nm, E_field_V_cm: pt.E_field }));
  const csvNet = profiles.map(pt => ({ position_nm: pt.x_nm, G_minus_R: pt.G - pt.R }));

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      height: '100%', width: '100%', overflow: 'hidden',
      background: 'var(--bg-0, #080b0f)',
    }}>
    <div style={{
      flex: 1, minHeight: 0,
      display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr',
      overflow: 'hidden',
    }}>

      {/* Carrier densities */}
      <div style={{ padding: '16px 20px', borderRight: '1px solid var(--border, #1e2d3d)', borderBottom: '1px solid var(--border, #1e2d3d)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div className="chart-title">Carrier Densities — n(x), p(x)</div>
        <div id="profile-chart-0" style={{ flex: 1, minHeight: 0 }}>
          {npData.length > 0 && (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={npData} margin={margin}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.plotGrid} />
                {sharedXAxis}
                <YAxis domain={[npMin, npMax]} tickFormatter={logTick} tick={tickStyle}
                  label={{ value: 'log\u2081\u2080 (cm\u207B\u00B3)', angle: -90, position: 'insideLeft', offset: -4, textAnchor: 'middle', ...axisStyle }} />
                <Tooltip content={<ProfileTooltip />} />
                <Line type="linear" dataKey="log_n" stroke={colors.accent} strokeWidth={2} dot={false} name="n" />
                <Line type="linear" dataKey="log_p" stroke={colors.amber}  strokeWidth={2} dot={false} name="p" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
        <PlotExportBar chartId="profile-chart-0" csvData={csvNP}
          csvFilename="profiles_carriers" plotFilename="profiles_carriers"
          c={c} disabled={profiles.length === 0} />
      </div>

      {/* Generation & Recombination */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border, #1e2d3d)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div className="chart-title">Generation G(x) and Recombination R(x)</div>
        <div id="profile-chart-1" style={{ flex: 1, minHeight: 0 }}>
          {grData.length > 0 && (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={grData} margin={margin}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.plotGrid} />
                {sharedXAxis}
                <YAxis domain={[grMin, grMax]} tickFormatter={logTick} tick={tickStyle}
                  label={{ value: 'log\u2081\u2080 (cm\u207B\u00B3s\u207B\u00B9)', angle: -90, position: 'insideLeft', offset: -4, textAnchor: 'middle', ...axisStyle }} />
                <Tooltip content={<ProfileTooltip />} />
                <Line type="linear" dataKey="log_G" stroke={colors.green} strokeWidth={2} dot={false} name="G" />
                <Line type="linear" dataKey="log_R" stroke={colors.red}   strokeWidth={2} dot={false} name="R" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
        <PlotExportBar chartId="profile-chart-1" csvData={csvGR}
          csvFilename="profiles_generation" plotFilename="profiles_generation"
          c={c} disabled={profiles.length === 0} />
      </div>

      {/* Electric field */}
      <div style={{ padding: '16px 20px', borderRight: '1px solid var(--border, #1e2d3d)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div className="chart-title">Electric Field E(x)</div>
        <div id="profile-chart-2" style={{ flex: 1, minHeight: 0 }}>
          {eData.length > 0 && (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={eData} margin={margin}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.plotGrid} />
                {sharedXAxis}
                <YAxis tickFormatter={linTick(0)} tick={tickStyle}
                  label={{ value: 'E (V/cm)', angle: -90, position: 'insideLeft', offset: -4, textAnchor: 'middle', ...axisStyle }} />
                <Tooltip content={<ProfileTooltip />} />
                <Line type="linear" dataKey="E" stroke={colors.accent} strokeWidth={2} dot={false} name="E" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
        <PlotExportBar chartId="profile-chart-2" csvData={csvE}
          csvFilename="profiles_efield" plotFilename="profiles_efield"
          c={c} disabled={csvE.length === 0} />
      </div>

      {/* Net generation G-R */}
      <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div className="chart-title">Net Generation G(x) - R(x)</div>
        <div id="profile-chart-3" style={{ flex: 1, minHeight: 0 }}>
          {netGRData.length > 0 && (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={netGRData} margin={margin}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.plotGrid} />
                {sharedXAxis}
                <YAxis tickFormatter={linTick(1)} tick={tickStyle}
                  label={{ value: 'G-R (cm\u207B\u00B3s\u207B\u00B9)', angle: -90, position: 'insideLeft', offset: -4, textAnchor: 'middle', ...axisStyle }} />
                <Tooltip content={<ProfileTooltip />} />
                <Line type="linear" dataKey="G-R" stroke={colors.red} strokeWidth={2} dot={false} name="G-R" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
        <PlotExportBar chartId="profile-chart-3" csvData={csvNet}
          csvFilename="profiles_netGR" plotFilename="profiles_netGR"
          c={c} disabled={profiles.length === 0} />
      </div>

    </div>
    </div>
  );
}
